/**
 * OrdersPage — Order history with 2-tab layout (Open / Closed)
 *
 * Open:   processing + in_transit orders (active, being fulfilled)
 * Closed: delivered + cancelled orders (completed)
 *
 * Each row: flat layout with order number, customer ref, date, total,
 * status badge, item count, expandable line items.
 *
 * Mock data from BrightStars Incorp. (demoData.js).
 *
 * @module pages/OrdersPage
 * @since 2026-05-25 (Group G — commerce flow)
 */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import { DEMO_ORDERS } from '@data/demoData';

const STATUS_CONFIG = {
  processing:  { label: 'Processing',  cls: 'bg-electric-blue text-white' },
  in_transit:  { label: 'In transit',  cls: 'bg-warning text-carbon-black' },
  delivered:   { label: 'Delivered',   cls: 'bg-success text-white' },
  cancelled:   { label: 'Cancelled',   cls: 'bg-n3 text-n6' },
};

const OPEN_STATUSES = ['processing', 'in_transit'];
const CLOSED_STATUSES = ['delivered', 'cancelled'];

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status];
  if (!config) return null;
  return (
    <span className={`inline-block px-2.5 py-0.5 text-xs font-normal ${config.cls}`}>
      {config.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function formatCurrency(amount, currency = 'CAD') {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(amount);
}

function OrderRow({ order }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <li className="border-b border-n2">
      <div
        className="flex items-center gap-4 py-4 px-2 hover:bg-n1 transition-colors cursor-pointer"
        onClick={() => setExpanded(prev => !prev)}
      >
        {/* Expand icon */}
        <button className="p-1 text-n4 flex-shrink-0" aria-label={expanded ? 'Collapse' : 'Expand'}>
          <span className="material-symbols-outlined text-lg">
            {expanded ? 'expand_less' : 'expand_more'}
          </span>
        </button>

        {/* Order info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Link
              to={`/orders/${order.orderId}`}
              onClick={e => e.stopPropagation()}
              className="text-sm font-normal text-electric-blue hover:underline"
            >
              {order.orderNumber}
            </Link>
            <StatusBadge status={order.status} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-n5">
            <span>Ordered {formatDate(order.orderDate)}</span>
            {order.customerRef && <span>Ref: {order.customerRef}</span>}
            {order.deliveryDate && <span>Delivered {formatDate(order.deliveryDate)}</span>}
            {order.estimatedDelivery && !order.deliveryDate && (
              <span>Est. delivery {formatDate(order.estimatedDelivery)}</span>
            )}
          </div>
        </div>

        {/* Total */}
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-normal text-carbon-black">
            {formatCurrency(order.totalAmount, order.currency)}
          </p>
          <p className="text-xs text-n5">
            {order.lines?.length || 0} item{order.lines?.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Detail link */}
        <Link
          to={`/orders/${order.orderId}`}
          onClick={e => e.stopPropagation()}
          className="text-n4 hover:text-electric-blue flex-shrink-0"
          aria-label="View order detail"
        >
          <span className="material-symbols-outlined text-xl">chevron_right</span>
        </Link>
      </div>

      {/* Expanded line items */}
      {expanded && order.lines && (
        <div className="px-10 pb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-n2 text-xs text-n5">
                <th className="text-left py-2 font-normal">Item</th>
                <th className="text-left py-2 font-normal">Description</th>
                <th className="text-right py-2 font-normal">Qty</th>
                <th className="text-right py-2 font-normal">Unit price</th>
                <th className="text-right py-2 font-normal">Line total</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map(line => (
                <tr key={line.lineNumber} className="border-b border-n1">
                  <td className="py-2">
                    <Link
                      to={`/product/${line.itemCode}`}
                      className="text-electric-blue hover:underline text-xs"
                    >
                      {line.itemCode}
                    </Link>
                  </td>
                  <td className="py-2 text-carbon-black">{line.description}</td>
                  <td className="py-2 text-right text-carbon-black">{line.qty}</td>
                  <td className="py-2 text-right text-n5">
                    {formatCurrency(line.unitPrice, order.currency)}
                  </td>
                  <td className="py-2 text-right font-normal text-carbon-black">
                    {formatCurrency(line.lineTotal, order.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {order.trackingNumber && (
            <p className="mt-3 text-xs text-n5">
              <span className="material-symbols-outlined text-sm align-middle mr-1">local_shipping</span>
              {order.carrier} — {order.trackingNumber}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

export default function OrdersPage() {
  const [activeTab, setActiveTab] = useState('open');
  const [sortOrder, setSortOrder] = useState('newest');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const openOrders = useMemo(
    () => DEMO_ORDERS.filter(o => OPEN_STATUSES.includes(o.status)),
    []
  );
  const closedOrders = useMemo(
    () => DEMO_ORDERS.filter(o => CLOSED_STATUSES.includes(o.status)),
    []
  );

  const filtered = useMemo(() => {
    let result = activeTab === 'open' ? [...openOrders] : [...closedOrders];

    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter(o => new Date(o.orderDate) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter(o => new Date(o.orderDate) <= to);
    }

    result.sort((a, b) => {
      const da = new Date(a.orderDate);
      const db = new Date(b.orderDate);
      return sortOrder === 'newest' ? db - da : da - db;
    });

    return result;
  }, [activeTab, sortOrder, dateFrom, dateTo, openOrders, closedOrders]);

  const tabs = [
    { key: 'open', label: 'Open', count: openOrders.length },
    { key: 'closed', label: 'Closed', count: closedOrders.length },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'My orders' }]} />
      <h1 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-6">
        My orders
      </h1>

      <div className="bg-white border border-n2 p-6">
        {/* Tabs */}
        <div className="border-b border-n2 mb-4">
          <nav className="flex gap-6" role="tablist">
            {tabs.map(tab => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`relative pb-3 text-sm font-normal transition-colors
                  ${activeTab === tab.key
                    ? 'text-electric-blue'
                    : 'text-n5 hover:text-carbon-black'}`}
              >
                {tab.label}
                <span className="ml-1.5 text-xs text-n5">({tab.count})</span>
                {activeTab === tab.key && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-blue" />
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <label className="text-xs text-n5">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="border border-n3 px-2 py-1 text-xs focus:ring-1 focus:ring-electric-blue"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-n5">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="border border-n3 px-2 py-1 text-xs focus:ring-1 focus:ring-electric-blue"
            />
          </div>
          <select
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value)}
            className="ml-auto border border-n3 px-2 py-1 text-xs focus:ring-1 focus:ring-electric-blue"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>

        {/* Order rows */}
        {filtered.length === 0 ? (
          <div className="text-center py-10">
            <span className="material-symbols-outlined text-3xl text-n3 block mb-2">receipt_long</span>
            <p className="text-sm text-n5">
              {activeTab === 'open'
                ? 'No open orders. All your orders have been fulfilled.'
                : 'No closed orders found for the selected period.'}
            </p>
          </div>
        ) : (
          <ul>
            {filtered.map(order => (
              <OrderRow key={order.orderId} order={order} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
